import React, { useContext, useEffect, useState } from 'react';
import { format } from 'date-fns';
import LockIcon from '@mui/icons-material/Lock';
import SecurityIcon from '@mui/icons-material/Security';
import NotificationsIcon from '@mui/icons-material/Notifications';
import PersonIcon from '@mui/icons-material/Person';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import BadgeIcon from '@mui/icons-material/Badge';
import EditIcon from '@mui/icons-material/Edit';
import {
  Card,
  CardContent,
  Button,
  Stack,
  Box,
  Typography,
  Grid,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  Divider,
  IconButton,
  Tooltip
} from '@mui/material';
import { Link, useNavigate } from 'react-router-dom';
import { ColorModeContext } from '../index';
import { request } from '../utils/request';
import ReusableHeader from '../components/Header/ReusableHeader';

/* =========================================================
   mEDGE design tokens (mirrors global.css :root values).
   ========================================================= */
const LIGHT_VARS = {
  '--vault-navy': '#0B2545',
  '--edge-blue': '#1F6FEB',
  '--canvas': '#FFFFFF',
  '--mist': '#F7F9FC',
  '--hairline': '#E4E9F0',
  '--blue-tint': '#EAF2FE',
  '--slate': '#6B7A90',
  '--secure': '#00A88F',
  '--high': '#E2622B',
  '--elevated': '#F0A202',
  '--critical': '#D6323A',
  '--border-radius': '6px',
};

const DARK_VARS = {
  '--vault-navy': '#F4F7FB',
  '--edge-blue': '#4C9AFF',
  '--canvas': '#0F1729',
  '--mist': '#252b3b',
  '--hairline': 'rgba(255,255,255,0.14)',
  '--blue-tint': 'rgba(76,154,255,0.16)',
  '--slate': '#93A4BD',
  '--secure': '#2FD3B8',
  '--high': '#F07C46',
  '--elevated': '#FFC24B',
  '--critical': '#FF6B72',
  '--border-radius': '6px',
};

function Profile() {
  const { mode } = useContext(ColorModeContext || { mode: 'light' });
  const V = mode === 'dark' ? DARK_VARS : LIGHT_VARS;
  const navigate = useNavigate();

  const storedUser = typeof window !== 'undefined' ? (localStorage.getItem('user') || sessionStorage.getItem('user')) : null;
  const user = storedUser ? JSON.parse(storedUser) : {};

  // Helpers
  const capitalize = (s = '') =>
    String(s)
      .split(' ')
      .map(w => w.charAt(0).toUpperCase() + w.slice(1))
      .join(' ')
      .trim();

  // Name formatting: "Amlan D."
  const rawName = user.name || `${user.first_name || ''} ${user.last_name || ''}`.trim();
  let displayName = rawName || 'Unknown User';
  if (rawName) {
    const parts = rawName.trim().split(/\s+/);
    const f = parts[0] || '';
    const last = parts.slice(1).join(' ');
    if (last) {
      displayName = `${capitalize(f)} ${capitalize(last)[0] ? capitalize(last)[0] + '.' : ''}`;
    } else {
      displayName = capitalize(f);
    }
  }

  const role = user.role || '';
  const phone = user.mobile || user.phone || '—';
  const department = user.department || '—';
  const empId = user.emp_id || user.empId || user.empID || '—';
  const designation = user.designation || '—';

  // Avatar state
  const [avatarUrl, setAvatarUrl] = useState(null);
  const [avatarLoading, setAvatarLoading] = useState(false);

  useEffect(() => {
    let mounted = true;
    const userId = user && (user._id || user.id);

    const fetchAvatar = async () => {
      if (!userId) return;
      setAvatarLoading(true);
      try {
        // Calls your endpoint: GET /api/v1/user/:id/avatar
        const data = await request('user', { method: 'get', params: `${userId}/avatar` });
        // expected shape: { url, expiresAt }
        if (mounted && data && data.url) {
          setAvatarUrl(data.url);
        }
      } catch (err) {
        // keep default letter avatar on error
        console.error('Failed to fetch avatar signed URL', err);
        setAvatarUrl(null);
      } finally {
        if (mounted) setAvatarLoading(false);
      }
    };

    fetchAvatar();
    return () => {
      mounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storedUser]);

  // ---------------------------
  // New: fetch user's sessions and deviceIds
  // ---------------------------
  const [devices, setDevices] = useState([]); // [{ deviceId, lastSeen }]
  const [devicesLoading, setDevicesLoading] = useState(false);
  useEffect(() => {
    let mounted = true;
    const userId = user && (user._id || user.id);
    if (!userId) {
      setDevices([]);
      return;
    }

    const fetchSessions = async () => {
      setDevicesLoading(true);
      try {
        const res = await request('sessions', { method: 'get', query: { userId, limit: 100 } });
        const sessions = (res && res.data) || [];
        const map = sessions.reduce((acc, s) => {
          const rawDevice = (s.deviceId || s.deviceid || (s.devicePosture && s.devicePosture.deviceId) || '').toString().trim();
          if (!rawDevice) return acc;
          const device = rawDevice;
          const loginTime = s.loginTime ? new Date(s.loginTime) : (s.createdAt ? new Date(s.createdAt) : null);
          const prev = acc[device];
          if (!prev || (loginTime && loginTime > prev.lastSeen)) {
            acc[device] = { deviceId: device, lastSeen: loginTime || new Date(0) };
          }
          return acc;
        }, {});
        const list = Object.values(map)
          .sort((a, b) => b.lastSeen - a.lastSeen)
          .map(it => ({ ...it, lastSeen: it.lastSeen instanceof Date ? it.lastSeen.toISOString() : it.lastSeen }));
        if (mounted) setDevices(list);
      } catch (err) {
        console.error('Failed to fetch sessions for devices:', err);
        if (mounted) setDevices([]);
      } finally {
        if (mounted) setDevicesLoading(false);
      }
    };

    fetchSessions();

    return () => {
      mounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storedUser]);

  const goToEditProfile = () => {
    navigate('/user-management');
  };

  const ProfileSummary = ({ displayName, designation, email, phone }) => (
    <Card
      elevation={0}
      sx={{
        mb: 3,
        borderRadius: '16px',
        background: mode === 'dark' 
          ? 'linear-gradient(135deg, var(--mist) 0%, var(--canvas) 100%)' 
          : 'linear-gradient(135deg, #ffffff 0%, var(--blue-tint) 100%)',
        border: '1px solid var(--hairline)',
        boxShadow: '0 4px 24px rgba(0,0,0,0.04)',
      }}
    >
      <CardContent sx={{ p: { xs: 3, sm: 4 }, display: 'flex', alignItems: 'center', gap: 4, flexDirection: { xs: 'column', sm: 'row' } }}>
        <Box sx={{ position: 'relative', display: 'inline-block' }}>
          {avatarLoading ? (
            <Box
              sx={{
                width: 110,
                height: 110,
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: 'var(--mist)',
                border: '1px solid var(--hairline)'
              }}
            >
              <CircularProgress size={28} sx={{ color: 'var(--edge-blue)' }} />
            </Box>
          ) : avatarUrl ? (
            <Box
              component="img"
              src={avatarUrl}
              alt="profile"
              sx={{
                width: 110,
                height: 110,
                borderRadius: '50%',
                objectFit: 'cover',
                border: '4px solid var(--canvas)',
                boxShadow: '0 8px 16px rgba(0,0,0,0.1)',
              }}
            />
          ) : (
            <Box
              sx={{
                width: 110,
                height: 110,
                borderRadius: '50%',
                backgroundColor: 'var(--edge-blue)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#fff',
                fontSize: '2.5rem',
                border: '4px solid var(--canvas)',
                boxShadow: '0 8px 16px rgba(0,0,0,0.1)',
                textTransform: 'uppercase',
              }}
            >
              {displayName ? displayName[0] : 'U'}
            </Box>
          )}

          <Tooltip title="Edit profile">
            <IconButton
              size="small"
              onClick={goToEditProfile}
              sx={{
                position: 'absolute',
                right: 0,
                bottom: 0,
                bgcolor: 'var(--canvas)',
                border: '1px solid var(--hairline)',
                boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                '&:hover': { bgcolor: 'var(--mist)', transform: 'scale(1.05)' },
                color: 'var(--vault-navy)',
                width: 36,
                height: 36,
                transition: 'all 0.2s ease-in-out'
              }}
              aria-label="edit profile"
            >
              <EditIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
        </Box>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, textAlign: { xs: 'center', sm: 'left' } }}>
          <Typography
            variant="h4"
            component="h2"
            sx={{ fontWeight: 700, color: 'var(--vault-navy)' }}
          >
            {displayName}
          </Typography>
          <Typography variant="subtitle1" sx={{ color: 'var(--edge-blue)', fontWeight: 600, display: 'flex', alignItems: 'center', justifyContent: { xs: 'center', sm: 'flex-start' }, gap: 1 }}>
            <BadgeIcon fontSize="small" />
            {designation || role || 'Admin'}
          </Typography>
          
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mt: 1 }}>
            <Typography variant="body2" sx={{ color: 'var(--slate)', display: 'flex', alignItems: 'center', justifyContent: { xs: 'center', sm: 'flex-start' }, gap: 0.75 }}>
              <EmailIcon fontSize="small" />
              {email || '—'}
            </Typography>
            <Typography variant="body2" sx={{ color: 'var(--slate)', display: 'flex', alignItems: 'center', justifyContent: { xs: 'center', sm: 'flex-start' }, gap: 0.75 }}>
              <PhoneIcon fontSize="small" />
              {phone}
            </Typography>
          </Stack>
        </Box>
      </CardContent>
    </Card>
  );

  const DetailCard = ({ title, showEdit, items, icon }) => (
    <Card
      elevation={0}
      sx={{
        borderRadius: '16px',
        mb: 3,
        backgroundColor: 'var(--canvas)',
        border: '1px solid var(--hairline)',
        boxShadow: '0 4px 20px rgba(0,0,0,0.02)',
      }}
    >
      <CardContent sx={{ p: { xs: 3, sm: 4 } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3, borderBottom: '1px solid var(--hairline)', pb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            {icon && <Box sx={{ color: 'var(--edge-blue)', display: 'flex' }}>{icon}</Box>}
            <Typography variant="h6" sx={{ color: 'var(--vault-navy)', fontWeight: 600 }}>
              {title}
            </Typography>
          </Box>
          {showEdit && (
            <IconButton 
              size="small" 
              onClick={goToEditProfile} 
              sx={{ color: 'var(--edge-blue)', backgroundColor: 'var(--blue-tint)' }}
            >
              <EditIcon fontSize="small" />
            </IconButton>
          )}
        </Box>

        <Grid container spacing={2}>
          {items.map((item, idx) => (
            <Grid item xs={12} sm={6} md={4} key={idx}>
              <Box sx={{ 
                p: 2, 
                borderRadius: '10px', 
                backgroundColor: 'var(--mist)', 
                border: '1px solid var(--hairline)',
                display: 'flex', 
                flexDirection: 'column', 
                gap: 0.5,
                height: '100%',
                transition: 'all 0.2s ease',
                '&:hover': {
                  borderColor: 'var(--slate)',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.04)'
                }
              }}>
                <Typography
                  variant="caption"
                  sx={{ color: 'var(--slate)', fontSize: '0.75rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}
                >
                  {item.label}
                </Typography>
                <Typography variant="body1" sx={{ wordBreak: 'break-word', color: 'var(--vault-navy)', fontWeight: 500 }}>
                  {item.value || '—'}
                </Typography>
              </Box>
            </Grid>
          ))}
        </Grid>
      </CardContent>
    </Card>
  );

  return (
    <Box p={{ xs: 2, md: 3 }} style={V} sx={{ backgroundColor: 'var(--mist)', minHeight: '100vh', borderRadius: '12px' }}>
      <ReusableHeader
        title="My Profile"
        breadcrumbs={[{ label: "My Profile" }]}
      />
      <Box sx={{ mt: 2, maxWidth: 1000, mx: 'auto' }}>
        <ProfileSummary
          displayName={displayName}
          designation={designation}
          email={user.email}
          phone={phone}
        />
        
        <DetailCard
          title="Personal Information"
          icon={<PersonIcon />}
          showEdit="primary"
          items={[
            { label: 'First Name', value: user.first_name || displayName.split(' ')[0] },
            { label: 'Last Name', value: user.last_name || displayName.split(' ').slice(1).join(' ') },
            { label: 'Date of Birth', value: user.dob || '—' },
            { label: 'Email Address', value: user.email },
            { label: 'Phone Number', value: phone },
            { label: 'User Role', value: role },
          ]}
        />
        
        <DetailCard
          title="Account & Work Details"
          icon={<BadgeIcon />}
          showEdit="secondary"
          items={[
            { label: 'Employee ID', value: empId },
            { label: 'Department', value: department },
            { label: 'Designation', value: designation },
            { label: 'Username', value: user.username || user.name || '—' },
            { label: 'Status', value: user.isActive !== false ? 'Active' : 'Inactive' },
          ]}
        />

        <Card
          elevation={0}
          sx={{
            borderRadius: '16px',
            backgroundColor: 'var(--canvas)',
            border: '1px solid var(--hairline)',
            boxShadow: '0 4px 20px rgba(0,0,0,0.02)',
            mb: 3
          }}
        >
          <CardContent sx={{ p: { xs: 3, sm: 4 } }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3, borderBottom: '1px solid var(--hairline)', pb: 2 }}>
              <Box sx={{ color: 'var(--edge-blue)', display: 'flex' }}><SecurityIcon /></Box>
              <Typography variant="h6" sx={{ color: 'var(--vault-navy)', fontWeight: 600 }}>
                Login Devices
              </Typography>
            </Box>

            {devicesLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress size={28} sx={{ color: 'var(--edge-blue)' }} />
              </Box>
            ) : devices.length === 0 ? (
              <Typography variant="body2" sx={{ color: 'var(--slate)', py: 3, textAlign: 'center', backgroundColor: 'var(--mist)', borderRadius: '10px', border: '1px dashed var(--hairline)' }}>
                No active devices found for this user.
              </Typography>
            ) : (
              <List dense sx={{ p: 0, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {devices.map((d, idx) => (
                  <ListItem 
                    key={d.deviceId + '_' + idx}
                    disableGutters
                    sx={{ 
                      py: 1.5, 
                      px: 2, 
                      borderRadius: '10px', 
                      backgroundColor: 'var(--mist)',
                      border: '1px solid var(--hairline)',
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        borderColor: 'var(--slate)',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.04)'
                      }
                    }}
                  >
                    <Box sx={{ mr: 2, color: 'var(--edge-blue)', display: 'flex', alignItems: 'center', justifyContent: 'center', p: 1, backgroundColor: 'var(--blue-tint)', borderRadius: '8px' }}>
                      <LockIcon fontSize="small" />
                    </Box>
                    <ListItemText
                      primary={<Typography sx={{ fontWeight: 600, color: 'var(--vault-navy)', fontSize: '0.95rem' }}>{d.deviceId}</Typography>}
                      secondary={
                        <Typography variant="caption" sx={{ color: 'var(--slate)', fontWeight: 500 }}>
                          Last seen: {d.lastSeen ? format(new Date(d.lastSeen), 'PP p') : '—'}
                        </Typography>
                      }
                    />
                  </ListItem>
                ))}
              </List>
            )}
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
}

export default Profile;

